/**
 * EXPO → Soru Yönetimi (admin).
 *
 * "Proje To Do List" sihirbazının sorularını düzenler: başlık, açıklama,
 * çoklu seçim, görünüm (liste / açılır liste), koşul ve seçenekler
 * (etiket + metin kutusu + renk + desen görseli). Her seçeneğe "Alt dal"
 * ile SINIRSIZ derinlikte alt soru bağlanabilir; alt sorular editörde
 * girintili gösterilir. Değişiklikler debounce ile otomatik kaydedilir.
 */
import { useEffect, useRef, useState } from "react";
import {
  Plus,
  Trash2,
  X,
  GripVertical,
  GitBranch,
  CornerDownRight,
  Palette,
  ImagePlus,
  ListChecks,
  Loader2,
  Check,
  AlertTriangle,
  ChevronDown,
} from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import QuestionWizard, {
  questionDepth,
  condOptionIds,
} from "../../components/expo/QuestionWizard";

const newId = () => Math.random().toString(16).slice(2, 10);

/** Koşul hedefi çoklu seçim: parent'ın seçenekleri + "Tümü" seçeneği.
 *  Boş liste = Tümü (parent'ta herhangi bir seçenek seçilince görünür). */
function CondOptionsPicker({ parent, value, onChange, testId }) {
  const ids = value || [];
  const isAll = ids.length === 0;
  const opts = parent?.options || [];
  const label = isAll
    ? "Tümü (her seçenekte)"
    : opts
        .filter((o) => ids.includes(o.id))
        .map((o) => o.label || "?")
        .join(", ") || "Seçenek seçin";
  const toggle = (oid) => {
    if (ids.includes(oid)) onChange(ids.filter((x) => x !== oid));
    else onChange([...ids, oid]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex h-8 w-56 items-center justify-between gap-1 rounded-md border border-zinc-200 bg-white px-2 text-left text-xs text-zinc-800 hover:bg-zinc-50"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50">
          <Checkbox
            checked={isAll}
            onCheckedChange={(v) => {
              if (v) onChange([]);
            }}
            data-testid={`${testId}-all`}
          />
          Tümü — hangi seçenek seçilirse seçilsin
        </label>
        <div className="my-1 h-px bg-zinc-100" />
        {opts.map((o) => (
          <label
            key={o.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1.5 text-xs hover:bg-zinc-50"
          >
            <Checkbox
              checked={!isAll && ids.includes(o.id)}
              onCheckedChange={() => toggle(o.id)}
              data-testid={`${testId}-opt-${o.id}`}
            />
            <span className={isAll ? "text-zinc-400" : "text-zinc-700"}>
              {o.label || "?"}
            </span>
          </label>
        ))}
        <p className="mt-1 px-1.5 text-[10px] leading-snug text-zinc-400">
          Birden fazla seçenek işaretleyebilirsiniz. Hiçbiri işaretli değilse
          "Tümü" geçerli olur.
        </p>
      </PopoverContent>
    </Popover>
  );
}

const isValid = (questions) =>
  questions.length > 0 &&
  questions.every(
    (q) =>
      q.title.trim() &&
      (q.options || []).length > 0 &&
      q.options.every((o) => o.label.trim()),
  );

/** Bir sorunun TÜM alt dallarının (torunlar dahil) id kümesi. */
const descendantIds = (rootId, questions) => {
  const ids = new Set();
  let changed = true;
  while (changed) {
    changed = false;
    for (const q of questions) {
      const pid = q.condition?.question_id;
      if (pid && (pid === rootId || ids.has(pid)) && !ids.has(q.id)) {
        ids.add(q.id);
        changed = true;
      }
    }
  }
  return ids;
};

/** Görseli küçültüp (max 256px) data-URI'ye çevirir. */
const fileToDataUri = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = fr.result;
    };
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

function SaveStatus({ state }) {
  if (state === "saving")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid="expo-q-save-status">
        <Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…
      </span>
    );
  if (state === "saved")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600" data-testid="expo-q-save-status">
        <Check className="h-3 w-3" /> Otomatik kaydedildi
      </span>
    );
  if (state === "invalid")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid="expo-q-save-status">
        <AlertTriangle className="h-3 w-3" /> Başlığı veya seçeneği boş soru var — tamamlanmadan kaydedilmez
      </span>
    );
  if (state === "error")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-600" data-testid="expo-q-save-status">
        <AlertTriangle className="h-3 w-3" /> Kaydedilemedi
      </span>
    );
  return (
    <span className="text-[11px] text-zinc-400" data-testid="expo-q-save-status">
      Değişiklikler otomatik kaydedilir
    </span>
  );
}

export default function ExpoQuestions() {
  const [questions, setQuestions] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [previewAnswers, setPreviewAnswers] = useState({});
  const timerRef = useRef(null);
  const qRef = useRef(null);
  qRef.current = questions;

  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    api.get("/expo/questions").then(({ data }) =>
      setQuestions(
        (data.questions || []).map((q) => ({
          ...q,
          prompt: q.prompt || "",
          display: q.display === "dropdown" ? "dropdown" : "list",
          // Eski tekil option_id biçimini çoklu option_ids'e dönüştür;
          // boş liste = "Tümü".
          condition: q.condition?.question_id
            ? {
                question_id: q.condition.question_id,
                option_ids: Array.isArray(q.condition.option_ids)
                  ? q.condition.option_ids
                  : q.condition.option_id
                    ? [q.condition.option_id]
                    : [],
              }
            : null,
          options: (q.options || []).map((o) => ({ ...o })),
        })),
      ),
    );
  }, []);

  const scheduleSave = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const qs = qRef.current || [];
      if (!isValid(qs)) {
        setSaveState("invalid");
        return;
      }
      setSaveState("saving");
      try {
        await api.put("/expo/questions", { questions: qs });
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 800);
  };

  const patch = (next) => {
    setQuestions(next);
    setTimeout(scheduleSave, 0);
  };

  const setQ = (qi, p) =>
    patch(questions.map((q, i) => (i === qi ? { ...q, ...p } : q)));

  const moveQuestion = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...questions];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    patch(next);
  };

  /** Seçeneğe bağlı yeni alt soru — ebeveynin son alt dalından hemen
   *  sonra eklenir, koşulu otomatik kurulur. */
  const addSubQuestion = (q, o) => {
    const child = {
      id: newId(),
      title: "",
      prompt: "",
      multi: false,
      display: "list",
      condition: { question_id: q.id, option_ids: [o.id] },
      options: [{ id: newId(), label: "", has_text: false, color: null, image: null }],
    };
    const desc = descendantIds(q.id, questions);
    let insertAt = questions.findIndex((x) => x.id === q.id);
    questions.forEach((x, i) => {
      if (desc.has(x.id) && i > insertAt) insertAt = i;
    });
    const next = [...questions];
    next.splice(insertAt + 1, 0, child);
    patch(next);
  };

  const removeQuestion = (qi) => {
    const q = questions[qi];
    const desc = descendantIds(q.id, questions);
    const msg =
      desc.size > 0
        ? `"${q.title || "Soru"}" ve ${desc.size} alt dalı silinsin mi?`
        : `"${q.title || "Soru"}" silinsin mi?`;
    if (!window.confirm(msg)) return;
    patch(questions.filter((x) => x.id !== q.id && !desc.has(x.id)));
  };

  const setOpt = (qi, oi, p) => {
    const q = questions[qi];
    const options = q.options.map((x, j) => (j === oi ? { ...x, ...p } : x));
    setQ(qi, { options });
  };

  const onPickImage = async (e, qi, oi) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataUri = await fileToDataUri(file);
      setOpt(qi, oi, { image: dataUri });
    } catch {
      window.alert("Görsel okunamadı");
    }
  };

  if (!questions)
    return (
      <div className="flex h-40 items-center justify-center text-zinc-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="expo-questions-page">
      <div>
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-violet-600" />
          <h1 className="font-display text-2xl font-semibold tracking-tight text-zinc-900">
            Soru Yönetimi
          </h1>
        </div>
        <p className="mt-1 text-sm text-zinc-500">
          "Proje To Do List" sihirbazının sorularını düzenleyin. Bir seçeneğin
          yanındaki <b>Alt dal</b> ile ona bağlı alt soru eklersiniz — alt
          dallar sınırsız derinlikte iç içe kurulabilir. Seçilen her cevap,
          projede bir görev maddesine dönüşür.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <SaveStatus state={saveState} />
      </div>

      {questions.map((q, qi) => {
        const depth = Math.min(questionDepth(q, questions), 3);
        const parent = q.condition?.question_id
          ? questions.find((x) => x.id === q.condition.question_id)
          : null;
        const condIds = parent ? condOptionIds(q.condition) : null;
        const condLabel = parent
          ? condIds
            ? (parent.options || [])
                .filter((o) => condIds.includes(o.id))
                .map((o) => o.label || "?")
                .join(", ") || "?"
            : "Tümü"
          : "";
        return (
        <div
          key={q.id}
          style={depth ? { marginLeft: depth * 24 } : undefined}
          onDragOver={(e) => {
            if (dragIndex.current == null) return;
            e.preventDefault();
            setDragOver(qi);
          }}
          onDragLeave={() => setDragOver((v) => (v === qi ? null : v))}
          onDrop={(e) => {
            if (dragIndex.current == null) return;
            e.preventDefault();
            moveQuestion(dragIndex.current, qi);
            dragIndex.current = null;
            setDragOver(null);
          }}
          className={`rounded-xl border bg-white p-4 shadow-sm transition-shadow ${
            depth ? "border-violet-200" : "border-zinc-200"
          } ${
            dragOver === qi && dragIndex.current !== qi ? "ring-2 ring-violet-400" : ""
          }`}
          data-testid={`expo-q-card-${qi}`}
        >
          {parent && (
            <div
              className="mb-2 inline-flex max-w-full items-center gap-1 truncate rounded-md bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700"
              data-testid={`expo-q-parent-badge-${qi}`}
              title={`Bu soru yalnızca "${parent.title || "?"}" = ${condLabel === "Tümü" ? "herhangi bir seçenek" : `"${condLabel}"`} seçilince görünür`}
            >
              <CornerDownRight className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {parent.title || "?"} = {condLabel}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span
              draggable
              onDragStart={(e) => {
                dragIndex.current = qi;
                e.dataTransfer.effectAllowed = "move";
                const card = e.currentTarget.closest("[data-testid^='expo-q-card-']");
                if (card) e.dataTransfer.setDragImage(card, 24, 24);
              }}
              onDragEnd={() => {
                dragIndex.current = null;
                setDragOver(null);
              }}
              title="Sürükleyerek sırala"
              className="cursor-grab rounded p-1 text-zinc-300 hover:bg-zinc-100 hover:text-zinc-500 active:cursor-grabbing"
              data-testid={`expo-q-drag-${qi}`}
            >
              <GripVertical className="h-4 w-4" />
            </span>
            <span className="w-5 text-right text-[11px] text-zinc-400">{qi + 1}.</span>
            <Input
              value={q.title}
              onChange={(e) => setQ(qi, { title: e.target.value })}
              placeholder="Soru başlığı (ör. Zemin)"
              className="flex-1 font-medium"
              data-testid={`expo-q-title-${qi}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeQuestion(qi)}
              title="Soruyu (ve alt dallarını) sil"
              className="text-zinc-400 hover:bg-red-50 hover:text-red-600"
              data-testid={`expo-q-remove-${qi}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 pl-12">
            <Input
              value={q.prompt}
              onChange={(e) => setQ(qi, { prompt: e.target.value })}
              placeholder="Açıklama (opsiyonel)"
              className="h-8 w-full max-w-xs text-sm"
              data-testid={`expo-q-prompt-${qi}`}
            />
            <label className="flex items-center gap-2 text-xs text-zinc-600">
              <Checkbox
                checked={!!q.multi}
                onCheckedChange={(v) =>
                  setQ(qi, {
                    multi: !!v,
                    ...(v ? { display: "list" } : {}),
                  })
                }
                data-testid={`expo-q-multi-${qi}`}
              />
              Çoklu seçim
            </label>
            <Select
              value={q.display === "dropdown" ? "dropdown" : "list"}
              onValueChange={(v) => setQ(qi, { display: v })}
            >
              <SelectTrigger
                className="h-8 w-48 text-xs"
                data-testid={`expo-q-display-${qi}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="list">Liste (kutucuklar)</SelectItem>
                <SelectItem value="dropdown" disabled={!!q.multi}>
                  Açılır liste (dropdown)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Koşul */}
          <div className="mt-2 flex flex-wrap items-center gap-2 pl-12">
            <span className="text-xs text-zinc-500">Koşul:</span>
            <Select
              value={q.condition?.question_id || "__always__"}
              onValueChange={(v) => {
                if (v === "__always__") setQ(qi, { condition: null });
                else {
                  const src = questions.find((x) => x.id === v);
                  setQ(qi, {
                    condition: {
                      question_id: v,
                      option_ids: src?.options?.[0]?.id
                        ? [src.options[0].id]
                        : [],
                    },
                  });
                }
              }}
            >
              <SelectTrigger className="h-8 w-56 text-xs" data-testid={`expo-q-cond-q-${qi}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__always__">Her zaman göster</SelectItem>
                {questions.slice(0, qi).map((pq) => (
                  <SelectItem key={pq.id} value={pq.id}>
                    "{pq.title || "?"}" sorusuna bağlı
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {q.condition?.question_id && (
              <>
                <span className="text-xs text-zinc-400">=</span>
                <CondOptionsPicker
                  parent={questions.find(
                    (x) => x.id === q.condition.question_id,
                  )}
                  value={q.condition.option_ids || []}
                  onChange={(ids) =>
                    setQ(qi, {
                      condition: {
                        question_id: q.condition.question_id,
                        option_ids: ids,
                      },
                    })
                  }
                  testId={`expo-q-cond-o-${qi}`}
                />
              </>
            )}
          </div>

          {/* Seçenekler */}
          <div className="mt-3 space-y-1.5 pl-12">
            {(q.options || []).map((o, oi) => {
              const childCount = questions.filter(
                (x) =>
                  x.condition?.question_id === q.id &&
                  x.condition?.option_id === o.id,
              ).length;
              return (
              <div key={o.id} className="flex flex-wrap items-center gap-2">
                <span className="w-4 text-right text-[11px] text-zinc-400">•</span>
                <Input
                  value={o.label}
                  onChange={(e) => setOpt(qi, oi, { label: e.target.value })}
                  placeholder="Seçenek etiketi"
                  className="h-8 min-w-[140px] flex-1 text-sm"
                  data-testid={`expo-q-opt-${qi}-${oi}`}
                />
                {/* Renk */}
                {o.color ? (
                  <span className="flex items-center">
                    <input
                      type="color"
                      value={o.color}
                      onChange={(e) => setOpt(qi, oi, { color: e.target.value })}
                      title="Renk"
                      className="h-6 w-7 cursor-pointer rounded border border-zinc-200 bg-white p-0.5"
                      data-testid={`expo-q-opt-color-${qi}-${oi}`}
                    />
                    <button
                      type="button"
                      onClick={() => setOpt(qi, oi, { color: null })}
                      title="Rengi kaldır"
                      className="rounded p-0.5 text-zinc-400 hover:text-red-600"
                      data-testid={`expo-q-opt-color-clear-${qi}-${oi}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setOpt(qi, oi, { color: "#7c3aed" })}
                    title="Renk ekle"
                    className="rounded p-1 text-zinc-400 hover:bg-violet-50 hover:text-violet-600"
                    data-testid={`expo-q-opt-color-add-${qi}-${oi}`}
                  >
                    <Palette className="h-3.5 w-3.5" />
                  </button>
                )}
                {/* Desen / görsel */}
                {o.image ? (
                  <span className="flex items-center">
                    <img
                      src={o.image}
                      alt=""
                      className="h-6 w-6 rounded border border-zinc-200 object-cover"
                      data-testid={`expo-q-opt-image-thumb-${qi}-${oi}`}
                    />
                    <button
                      type="button"
                      onClick={() => setOpt(qi, oi, { image: null })}
                      title="Deseni kaldır"
                      className="rounded p-0.5 text-zinc-400 hover:text-red-600"
                      data-testid={`expo-q-opt-image-clear-${qi}-${oi}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ) : (
                  <label
                    title="Desen / görsel ekle"
                    className="cursor-pointer rounded p-1 text-zinc-400 hover:bg-violet-50 hover:text-violet-600"
                    data-testid={`expo-q-opt-image-${qi}-${oi}`}
                  >
                    <ImagePlus className="h-3.5 w-3.5" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onPickImage(e, qi, oi)}
                    />
                  </label>
                )}
                <label
                  className="flex items-center gap-1.5 text-[11px] text-zinc-500"
                  title="İşaretlenirse yanında serbest metin kutusu açılır"
                >
                  <Checkbox
                    checked={!!o.has_text}
                    onCheckedChange={(v) => setOpt(qi, oi, { has_text: !!v })}
                    data-testid={`expo-q-opt-text-${qi}-${oi}`}
                  />
                  metin
                </label>
                <button
                  type="button"
                  onClick={() => addSubQuestion(q, o)}
                  title="Bu seçeneğe bağlı alt dal (alt soru) ekle — sınırsız derinlik"
                  className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[11px] font-medium text-violet-700 hover:bg-violet-100"
                  data-testid={`expo-q-opt-subq-${qi}-${oi}`}
                >
                  <GitBranch className="h-3 w-3" />
                  Alt dal{childCount > 0 ? ` (${childCount})` : ""}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setQ(qi, { options: q.options.filter((_, j) => j !== oi) })
                  }
                  className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                  data-testid={`expo-q-opt-remove-${qi}-${oi}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              );
            })}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setQ(qi, {
                  options: [
                    ...(q.options || []),
                    { id: newId(), label: "", has_text: false, color: null, image: null },
                  ],
                })
              }
              className="h-7 text-xs text-zinc-500"
              data-testid={`expo-q-opt-add-${qi}`}
            >
              <Plus className="mr-1 h-3 w-3" /> Seçenek ekle
            </Button>
          </div>
        </div>
        );
      })}

      <Button
        type="button"
        variant="outline"
        onClick={() =>
          patch([
            ...questions,
            {
              id: newId(),
              title: "",
              prompt: "",
              multi: false,
              display: "list",
              condition: null,
              options: [{ id: newId(), label: "", has_text: false, color: null, image: null }],
            },
          ])
        }
        className="w-full border-dashed"
        data-testid="expo-q-add"
      >
        <Plus className="mr-1.5 h-4 w-4" /> Soru ekle
      </Button>

      {/* Canlı önizleme */}
      <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-violet-600">
          Önizleme — form böyle görünecek
        </div>
        <QuestionWizard
          questions={questions}
          answers={previewAnswers}
          onChange={setPreviewAnswers}
        />
      </div>
    </div>
  );
}
